import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { naira, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Format USD cents → "$1.23" with always two decimals so the analytics
// panel never shows a lonely "$0" that looks like a missing figure.
const usd = (cents = 0) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

// Lightweight inline sparkline. Scales ``values`` into an SVG polyline
// across a 300×48 viewport. We render even a flat zero series as a
// baseline line so the chart never looks "broken".
function Sparkline({ values, color = "#D4AF37", testId }) {
  const W = 300, H = 48, PAD = 2;
  const n = Math.max(values.length, 2);
  const max = Math.max(1, ...values);
  const pts = values.map((v, i) => {
    const x = PAD + (i * (W - PAD * 2)) / (n - 1);
    const y = H - PAD - (Math.max(0, v) / max) * (H - PAD * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  // Area fill — same points but closed to the baseline for a soft wash under the line.
  const first = pts.split(" ")[0]?.split(",")[0] || PAD;
  const last = pts.split(" ").slice(-1)[0]?.split(",")[0] || (W - PAD);
  const area = `${first},${H} ${pts} ${last},${H}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" data-testid={testId}>
      <polyline points={area} fill={color} fillOpacity="0.12" stroke="none" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// Trigger a CSV download from the API. The browser needs real cookies
// to be sent (so axios won't do; we use fetch + credentials: include).
// Falls back to a toast if the server returns an auth error.
async function downloadCsv(path, filename) {
  try {
    const res = await fetch(`${API}${path}`, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    // Minimal toast fallback — using a plain alert avoids a sonner import here.
    alert("Download failed. Please refresh and try again.");
  }
}
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Film, Eye, DollarSign, Clock, CheckCircle2, XCircle, Sparkles, Pencil, Receipt, Users, Heart, Globe, Download } from "lucide-react";
import FestivalRequestTimeline from "@/components/FestivalRequestTimeline";
import ProfileLinkCard from "@/components/ProfileLinkCard";
import WalletCard from "@/components/WalletCard";
import { StarRatingDisplay } from "@/components/StarRating";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function FilmmakerDashboard() {
  const { user } = useAuth();
  const [films, setFilms] = useState([]);
  const [requests, setRequests] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payingInvoiceId, setPayingInvoiceId] = useState(null);
  // Rating summary keyed by film id — fetched lazily after the films list
  // loads so a filmmaker with 30 films doesn't wait on 30 sequential calls.
  const [ratingsByFilm, setRatingsByFilm] = useState({});
  // Full financial roll-up for the analytics panel. Always-visible zero
  // defaults so sections never pop in; backend returns the same shape
  // even on a brand-new account.
  const [earnings, setEarnings] = useState({
    rentals: { local_naira: 0, local_count: 0, african_cents: 0, african_count: 0, diaspora_cents: 0, diaspora_count: 0 },
    watch_party: { naira: 0, usd_cents: 0, sessions: 0 },
    donations: { count: 0, gross_naira: 0, net_naira: 0, transactions: [] },
    regional: { local_naira: 0, african_cents: 0, diaspora_cents: 0 },
    totals: { naira: 0, usd_cents: 0 },
  });
  // 30-day earnings sparkline (NGN + USD lines over time). Fetched
  // separately so the main breakdown panel paints instantly while this
  // trails in a beat later.
  const [trend, setTrend] = useState({ days: 30, points: [], totals: { naira: 0, usd_cents: 0 } });

  useEffect(() => {
    api.get("/films/my").then((r) => {
      const list = r.data || [];
      setFilms(list);
      // Parallel-fetch each film's rating summary. Failures are silent —
      // "no reviews" simply renders an empty state on the card.
      Promise.all(
        list.map((f) =>
          api.get(`/films/${f.id}/ratings/summary`)
            .then((rr) => [f.id, rr.data])
            .catch(() => [f.id, null])
        )
      ).then((rows) => {
        const map = {};
        for (const [fid, summary] of rows) {
          if (summary) map[fid] = summary;
        }
        setRatingsByFilm(map);
      });
    }).catch(() => {});
    api.get("/festivals/my-requests").then((r) => setRequests(r.data || [])).catch(() => {});
    api.get("/festival-invoices/me").then((r) => setInvoices(r.data || [])).catch(() => {});
    api.get("/me/earnings/breakdown").then((r) => setEarnings(r.data)).catch(() => {});
    api.get("/me/earnings/trend?days=30").then((r) => setTrend(r.data)).catch(() => {});
  }, []);

  const payInvoice = async (invoiceId) => {
    setPayingInvoiceId(invoiceId);
    try {
      const callback_url = `${window.location.origin}/festival-invoice/callback`;
      const { data } = await api.post(`/festival-invoices/${invoiceId}/pay`, { callback_url });
      if (data.already_paid) {
        toast.success("This invoice is already paid.");
        setPayingInvoiceId(null);
        return;
      }
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start payment");
      setPayingInvoiceId(null);
    }
  };

  const totalViews = films.reduce((s, f) => s + (f.views || 0), 0);
  const totalPurchases = films.reduce((s, f) => s + (f.purchases || 0), 0);
  const approved = films.filter((f) => f.status === "approved").length;

  const stat = (label, value, icon) => (
    <div className="p-5 rounded-xl bg-surface border border-white/10">
      <div className="flex items-center justify-between mb-2 text-foreground/60">
        <span className="text-xs uppercase tracking-wider">{label}</span>
        {icon}
      </div>
      <p className="font-display text-2xl font-black">{value}</p>
    </div>
  );

  const statusBadge = (s) => {
    const map = {
      pending: { cls: "bg-gold/15 text-gold border-gold/30", label: "In review", icon: Clock },
      approved: { cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", label: "Live", icon: CheckCircle2 },
      rejected: { cls: "bg-red-500/15 text-red-400 border-red-500/30", label: "Rejected", icon: XCircle },
    };
    const m = map[s] || map.pending;
    const Icon = m.icon;
    return (
      <Badge className={`${m.cls} border gap-1`}>
        <Icon className="w-3 h-3" /> {m.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-ink grain">
      <Navbar />
      <div className="pt-28 px-6 sm:px-12 lg:px-20 max-w-[1600px] mx-auto pb-20" data-testid="filmmaker-dashboard">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1">Filmmaker</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black">Your studio</h1>
            {user?.name && <p className="text-sm text-foreground/60 mt-2" data-testid="dashboard-filmmaker-name">{user.name}</p>}
          </div>
          <Link to="/filmmaker/upload">
            <Button className="bg-terracotta hover:bg-ember text-white h-11 px-6" data-testid="upload-new-film-btn">
              <Plus className="w-4 h-4" /> Upload new film
            </Button>
          </Link>
        </div>

        {/* ============================================================
            ANALYTICS — complete financial overview, always visible
            even at zero. Layered from top to bottom:
              1. Overall Revenue (Naira + USD split)
              2. Regional Earnings (3 cards)
              3. Rentals breakdown by region
              4. Watch Party revenue (NGN + USD)
              5. Donations (gross/net + transaction list)
            ============================================================ */}
        <section className="mb-10 space-y-4" data-testid="filmmaker-analytics">
          {/* --- 30-day earnings trend (sparkline) --- */}
          <div className="rounded-2xl border border-white/10 bg-surface p-5" data-testid="analytics-trend">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-foreground/60" />
                <h2 className="text-xs uppercase tracking-widest text-foreground/60 font-semibold">Last 30 days</h2>
              </div>
              <p className="text-[11px] text-foreground/50">
                {naira(trend.totals.naira)} · {usd(trend.totals.usd_cents)}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1 text-[10px] uppercase tracking-wider text-foreground/55">
                  <span>Naira trend</span>
                  <span className="font-display font-bold text-gold text-xs normal-case tracking-normal">{naira(trend.totals.naira)}</span>
                </div>
                <Sparkline values={trend.points.map((p) => p.naira)} color="#D4AF37" testId="sparkline-naira" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1 text-[10px] uppercase tracking-wider text-foreground/55">
                  <span>USD trend</span>
                  <span className="font-display font-bold text-ember text-xs normal-case tracking-normal">{usd(trend.totals.usd_cents)}</span>
                </div>
                <Sparkline values={trend.points.map((p) => p.usd_cents)} color="#C36A4A" testId="sparkline-usd" />
              </div>
            </div>
          </div>

          {/* --- Overall Revenue --- */}
          <div className="rounded-2xl border border-gold/30 bg-gradient-to-br from-gold/10 via-ember/5 to-transparent p-6" data-testid="analytics-overall">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-4 h-4 text-gold" />
              <h2 className="text-xs uppercase tracking-widest text-gold font-semibold">Overall revenue</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-foreground/55">Naira total</p>
                <p className="font-display text-4xl font-black mt-1" data-testid="overall-naira">
                  {naira(earnings.totals.naira)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-foreground/55">USD total</p>
                <p className="font-display text-4xl font-black mt-1" data-testid="overall-usd">
                  {usd(earnings.totals.usd_cents)}
                </p>
              </div>
            </div>
            <p className="text-[11px] text-foreground/50 mt-4">
              Shown separately — we don't FX-convert, so this is what will actually land in your bank account.
            </p>
          </div>

          {/* --- Four KPI badges (films, views, rentals, lifetime overall) --- */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stat("Live films", approved, <Film className="w-4 h-4" />)}
            {stat("Paid views", totalViews, <Eye className="w-4 h-4" />)}
            {stat("Total rentals", totalPurchases, <CheckCircle2 className="w-4 h-4" />)}
            {stat("Donation tips", earnings.donations.count, <Heart className="w-4 h-4 text-pink-300" />)}
          </div>

          {/* --- Regional earnings --- */}
          <div>
            <div className="flex items-center gap-2 mb-3 mt-2">
              <Globe className="w-4 h-4 text-foreground/60" />
              <h2 className="text-xs uppercase tracking-widest text-foreground/60 font-semibold">Regional earnings</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="analytics-regional">
              <div className="rounded-xl p-5 bg-surface border border-emerald-400/20" data-testid="regional-local">
                <p className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">Nigeria (Local)</p>
                <p className="font-display text-2xl font-black mt-1">{naira(earnings.regional.local_naira)}</p>
                <p className="text-[11px] text-foreground/50 mt-1">Rentals + Watch Party + donations</p>
              </div>
              <div className="rounded-xl p-5 bg-surface border border-sky-400/20" data-testid="regional-african">
                <p className="text-[10px] uppercase tracking-wider text-sky-300 font-semibold">Africa (Rest)</p>
                <p className="font-display text-2xl font-black mt-1">{usd(earnings.regional.african_cents)}</p>
                <p className="text-[11px] text-foreground/50 mt-1">Rentals at the discounted tier</p>
              </div>
              <div className="rounded-xl p-5 bg-surface border border-violet-400/20" data-testid="regional-diaspora">
                <p className="text-[10px] uppercase tracking-wider text-violet-300 font-semibold">Diaspora</p>
                <p className="font-display text-2xl font-black mt-1">{usd(earnings.regional.diaspora_cents)}</p>
                <p className="text-[11px] text-foreground/50 mt-1">Rentals + Watch Party (USD)</p>
              </div>
            </div>
          </div>

          {/* --- Rentals (Pay-per-view) breakdown --- */}
          <div>
            <div className="flex items-center justify-between mb-3 mt-2 gap-2">
              <div className="flex items-center gap-2">
                <Receipt className="w-4 h-4 text-foreground/60" />
                <h2 className="text-xs uppercase tracking-widest text-foreground/60 font-semibold">Rentals · pay-per-view</h2>
              </div>
              <button
                type="button"
                onClick={() => downloadCsv("/me/export/rentals.csv", "cinevoya-rentals.csv")}
                className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full border border-white/15 bg-ink text-[11px] font-semibold text-foreground/80 hover:text-foreground hover:border-gold/60 transition-colors"
                data-testid="rentals-csv-btn"
              >
                <Download className="w-3 h-3" /> CSV
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="analytics-rentals">
              <div className="rounded-xl p-5 bg-surface border border-white/10" data-testid="rentals-local">
                <p className="text-[10px] uppercase tracking-wider text-foreground/55">Nigeria · ₦</p>
                <p className="font-display text-2xl font-black mt-1">{naira(earnings.rentals.local_naira)}</p>
                <p className="text-[11px] text-foreground/50 mt-1">
                  {earnings.rentals.local_count || 0} rental{earnings.rentals.local_count === 1 ? "" : "s"}
                </p>
              </div>
              <div className="rounded-xl p-5 bg-surface border border-white/10" data-testid="rentals-african">
                <p className="text-[10px] uppercase tracking-wider text-foreground/55">African · $</p>
                <p className="font-display text-2xl font-black mt-1">{usd(earnings.rentals.african_cents)}</p>
                <p className="text-[11px] text-foreground/50 mt-1">
                  {earnings.rentals.african_count || 0} rental{earnings.rentals.african_count === 1 ? "" : "s"}
                </p>
              </div>
              <div className="rounded-xl p-5 bg-surface border border-white/10" data-testid="rentals-diaspora">
                <p className="text-[10px] uppercase tracking-wider text-foreground/55">Diaspora · $</p>
                <p className="font-display text-2xl font-black mt-1">{usd(earnings.rentals.diaspora_cents)}</p>
                <p className="text-[11px] text-foreground/50 mt-1">
                  {earnings.rentals.diaspora_count || 0} rental{earnings.rentals.diaspora_count === 1 ? "" : "s"}
                </p>
              </div>
            </div>
          </div>

          {/* --- Watch Party revenue --- */}
          <div>
            <div className="flex items-center justify-between mb-3 mt-2 gap-2">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-terracotta" />
                <h2 className="text-xs uppercase tracking-widest text-terracotta font-semibold">Watch Party revenue</h2>
              </div>
              <button
                type="button"
                onClick={() => downloadCsv("/me/export/watch-party.csv", "cinevoya-watch-party.csv")}
                className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full border border-terracotta/30 bg-terracotta/5 text-[11px] font-semibold text-terracotta hover:bg-terracotta/15 transition-colors"
                data-testid="wp-csv-btn"
              >
                <Download className="w-3 h-3" /> CSV
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="analytics-watch-party">
              <div className="rounded-xl p-5 bg-surface border border-terracotta/20">
                <p className="text-[10px] uppercase tracking-wider text-foreground/55">Naira</p>
                <p className="font-display text-2xl font-black mt-1" data-testid="wp-earnings-naira">{naira(earnings.watch_party.naira)}</p>
                <p className="text-[11px] text-foreground/50 mt-1">Your 70% share</p>
              </div>
              <div className="rounded-xl p-5 bg-surface border border-terracotta/20">
                <p className="text-[10px] uppercase tracking-wider text-foreground/55">USD</p>
                <p className="font-display text-2xl font-black mt-1" data-testid="wp-earnings-usd">{usd(earnings.watch_party.usd_cents)}</p>
                <p className="text-[11px] text-foreground/50 mt-1">Your 70% share</p>
              </div>
              <div className="rounded-xl p-5 bg-surface border border-terracotta/20">
                <p className="text-[10px] uppercase tracking-wider text-foreground/55">Sessions</p>
                <p className="font-display text-2xl font-black mt-1" data-testid="wp-session-count">{earnings.watch_party.sessions || 0}</p>
                <p className="text-[11px] text-foreground/50 mt-1">Hosted on your films</p>
              </div>
            </div>
          </div>

          {/* --- Donations --- */}
          <div>
            <div className="flex items-center justify-between mb-3 mt-2 gap-2">
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-pink-300" />
                <h2 className="text-xs uppercase tracking-widest text-pink-300 font-semibold">Donations</h2>
              </div>
              <button
                type="button"
                onClick={() => downloadCsv("/me/export/donations.csv", "cinevoya-donations.csv")}
                className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full border border-pink-400/30 bg-pink-500/5 text-[11px] font-semibold text-pink-300 hover:bg-pink-500/15 transition-colors"
                data-testid="donations-csv-btn"
              >
                <Download className="w-3 h-3" /> CSV
              </button>
            </div>
            <div className="rounded-xl border border-pink-400/20 bg-pink-500/5 overflow-hidden" data-testid="analytics-donations">
              <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-4 border-b border-pink-400/15">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-foreground/55">Gross received</p>
                  <p className="font-display text-2xl font-black mt-1" data-testid="donations-gross">{naira(earnings.donations.gross_naira)}</p>
                  <p className="font-display text-lg font-bold text-foreground/80" data-testid="donations-gross-usd">{usd(earnings.donations.gross_usd_cents)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-pink-300">Your 70% net</p>
                  <p className="font-display text-2xl font-black text-pink-200 mt-1" data-testid="donations-net">{naira(earnings.donations.net_naira)}</p>
                  <p className="font-display text-lg font-bold text-pink-200/80" data-testid="donations-net-usd">{usd(earnings.donations.net_usd_cents)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-foreground/55">Tips</p>
                  <p className="font-display text-2xl font-black mt-1" data-testid="donations-count">{earnings.donations.count}</p>
                </div>
              </div>
              {earnings.donations.transactions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-black/20 text-[10px] uppercase tracking-wider text-foreground/50">
                      <tr>
                        <th className="text-left p-3">Date</th>
                        <th className="text-left p-3">Donor</th>
                        <th className="text-left p-3">Film</th>
                        <th className="text-right p-3">Amount</th>
                        <th className="text-right p-3">Your 70%</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {earnings.donations.transactions.slice(0, 25).map((d) => (
                        <tr key={d.id} data-testid={`donation-row-${d.id}`}>
                          <td className="p-3 text-xs text-foreground/60 whitespace-nowrap">
                            {d.paid_at ? new Date(d.paid_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"}
                          </td>
                          <td className="p-3 text-xs text-foreground/80 max-w-[140px] truncate">{d.donor_name || "A fan"}</td>
                          <td className="p-3 text-xs text-foreground/70 max-w-[200px] truncate">{d.film_title || "—"}</td>
                          <td className="p-3 text-right font-semibold">{d.currency === "USD" ? usd(d.amount_usd_cents) : naira(d.amount_naira || 0)}</td>
                          <td className="p-3 text-right font-semibold text-pink-300">{d.currency === "USD" ? usd(d.creator_share_cents) : naira(d.creator_share_naira || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-6 text-center text-foreground/50 text-xs" data-testid="donations-empty">
                  No donations yet. When fans tip your films, each transaction will show up here with the 70% that landed in your wallet.
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <WalletCard />
          <ProfileLinkCard />
        </div>

        <h2 className="font-display text-2xl font-bold mb-4">Your films</h2>
        {films.length === 0 ? (
          <div className="p-10 rounded-xl bg-surface border border-white/10 text-center" data-testid="filmmaker-empty">
            <Film className="w-10 h-10 mx-auto text-foreground/20 mb-3" />
            <p className="text-foreground/60 mb-4">You haven&apos;t uploaded any films yet.</p>
            <Link to="/filmmaker/upload">
              <Button className="bg-terracotta hover:bg-ember text-white" data-testid="empty-upload-btn">
                Upload your first short
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {films.map((f) => (
              <div key={f.id} className="rounded-xl bg-surface border border-white/10 overflow-hidden flex flex-col" data-testid={`my-film-${f.id}`}>
                <div className="aspect-video bg-ink relative">
                  <img src={`${API}/films/${f.id}/poster`} alt={f.title} className="w-full h-full object-cover" />
                  <div className="absolute top-3 right-3 flex items-center gap-1.5">
                    {f.status === "approved" && f.publishing_state && f.publishing_state !== "public" && (
                      <span
                        className={[
                          "px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider flex items-center gap-0.5",
                          f.publishing_state === "premiering" && "bg-terracotta text-white",
                          f.publishing_state === "coming_soon" && "bg-sky-500/80 text-white",
                          f.publishing_state === "festival_run" && "bg-amber-500/80 text-white",
                          f.publishing_state === "ready" && "bg-foreground/30 text-ink",
                        ].filter(Boolean).join(" ")}
                        data-testid={`film-publishing-state-${f.id}`}
                      >
                        {f.publishing_state === "premiering" && <><Sparkles className="w-2.5 h-2.5" /> Premiere</>}
                        {f.publishing_state === "coming_soon" && "Coming Soon"}
                        {f.publishing_state === "festival_run" && "Festival Run"}
                        {f.publishing_state === "ready" && "Ready"}
                      </span>
                    )}
                    {statusBadge(f.status)}
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-display font-bold text-lg leading-tight mb-1 line-clamp-1">{f.title}</h3>
                  <p className="text-xs text-foreground/60 mb-3 capitalize">{f.genre} · {f.duration_minutes}m · {naira(f.price_naira)}</p>
                  {ratingsByFilm[f.id]?.count > 0 && (
                    <div className="flex items-center gap-2 mb-3" data-testid={`film-rating-${f.id}`}>
                      <StarRatingDisplay
                        value={ratingsByFilm[f.id].average}
                        size={14}
                        showValue
                        count={ratingsByFilm[f.id].count}
                      />
                    </div>
                  )}
                  {f.admin_note && (
                    <p className="text-xs text-foreground/70 italic mb-3 p-2 rounded bg-ink border border-white/5">&ldquo;{f.admin_note}&rdquo;</p>
                  )}
                  <div className="grid grid-cols-3 gap-2 text-center mt-auto pt-3 border-t border-white/5 text-xs">
                    <div>
                      <p className="font-display font-bold text-base">{f.views || 0}</p>
                      <p className="text-foreground/50">Paid views (₦)</p>
                    </div>
                    <div>
                      <p className="font-display font-bold text-base">{f.purchases || 0}</p>
                      <p className="text-foreground/50">Rentals (₦)</p>
                    </div>
                    <div>
                      <p className="font-display font-bold text-base text-gold">{naira(f.earnings || 0)}</p>
                      <p className="text-foreground/50">Earned (₦)</p>
                    </div>
                  </div>
                  {(f.purchases_usd || f.earnings_usd_cents) ? (
                    <div className="grid grid-cols-3 gap-2 text-center pt-2 text-xs" data-testid={`usd-stats-${f.id}`}>
                      <div>
                        <p className="font-display font-bold text-base text-emerald-300">{f.views_usd || 0}</p>
                        <p className="text-foreground/50">Paid views ($)</p>
                      </div>
                      <div>
                        <p className="font-display font-bold text-base text-emerald-300">{f.purchases_usd || 0}</p>
                        <p className="text-foreground/50">Rentals ($)</p>
                      </div>
                      <div>
                        <p className="font-display font-bold text-base text-emerald-400">${((f.earnings_usd_cents || 0) / 100).toFixed(2)}</p>
                        <p className="text-foreground/50">Earned ($)</p>
                      </div>
                    </div>
                  ) : null}
                  <div className="mt-3 flex gap-2">
                    <Link to={`/film/${f.id}`} className="flex-1">
                      <Button variant="outline" className="w-full border-white/10 hover:bg-white/5" size="sm" data-testid={`view-film-${f.id}`}>View</Button>
                    </Link>
                    {(f.status === "pending" || f.status === "rejected") && (
                      <Link to={`/filmmaker/edit/${f.id}`} className="flex-1">
                        <Button variant="outline" className="w-full border-white/10 hover:bg-white/5" size="sm" data-testid={`edit-film-${f.id}`}>
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </Button>
                      </Link>
                    )}
                    <Link to={`/filmmaker/analyze/${f.id}`} className="flex-1">
                      <Button className="w-full bg-terracotta hover:bg-ember text-white" size="sm" data-testid={`analyze-film-${f.id}`}>
                        <Sparkles className="w-3.5 h-3.5" /> AI festivals
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <h2 className="font-display text-2xl font-bold mt-12 mb-4">Festival submission requests</h2>
        {invoices.filter((i) => i.status === "pending").length > 0 && (
          <div className="mb-6 space-y-3" data-testid="festival-invoices-pending">
            {invoices.filter((i) => i.status === "pending").map((inv) => (
              <div
                key={inv.id}
                className="rounded-xl border border-gold/40 bg-gold/8 p-5 flex flex-wrap items-center justify-between gap-4"
                data-testid={`festival-invoice-${inv.id}`}
              >
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-full bg-gold/20 text-gold flex items-center justify-center shrink-0 mt-0.5">
                    <Receipt className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="font-semibold text-sm">
                      Service fee due · <span className="text-gold">{naira(inv.amount_naira)}</span>
                    </p>
                    <p className="text-xs text-foreground/65 mt-0.5 leading-relaxed max-w-md">
                      Cinevoya has approved your submission of <strong className="text-foreground/85">{inv.film_title}</strong> to <strong className="text-foreground/85">{inv.festival_name}</strong>.
                      Pay this service fee to start processing. <span className="text-foreground/50">(Festival entry fees are billed separately by the festival.)</span>
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => payInvoice(inv.id)}
                  disabled={payingInvoiceId === inv.id}
                  className="bg-gold hover:bg-gold/90 text-ink font-semibold h-10"
                  data-testid={`pay-invoice-${inv.id}`}
                >
                  {payingInvoiceId === inv.id ? "Redirecting…" : `Pay ${naira(inv.amount_naira)}`}
                </Button>
              </div>
            ))}
          </div>
        )}
        {requests.length === 0 ? (
          <p className="text-foreground/50 text-sm">No festival submission requests yet. Run an AI analysis on any film to discover qualifying festivals.</p>
        ) : (
          <div className="space-y-4">
            {requests.map((r) => (
              <FestivalRequestTimeline key={r.id} request={r} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
